import {
  Table as TableInterface,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  createColumnHelper,
  getSortedRowModel,
  RowSelectionState,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
  flexRender,
  TableState,
  ColumnDef,
  Column,
  Row,
} from '@tanstack/react-table';
import React from 'react';

import { TableHeader, TableHead, TableBody, TableCell, TableRow, Table } from '../table/table';
import { cn } from '../../utils/class-merge';
import { Loading } from '../loading/loading';

interface DataTableProps<T> {
  noResultsComponent?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  table: TableInterface<T>;
  columnsLength: number;
  hideHeader?: boolean;
  className?: string;
  isLoading: boolean;
}

const DataTable = <T,>({
  noResultsComponent,
  loadingComponent,
  columnsLength,
  hideHeader,
  isLoading,
  className,
  table,
}: DataTableProps<T>) => {
  return (
    <div className={cn('rounded-bl-lg rounded-br-lg', className)}>
      <Table>
        {!hideHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    style={{
                      maxWidth: header.column.columnDef.maxSize,
                      minWidth: header.column.columnDef.size,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={columnsLength}>
                {loadingComponent || <Loading className="p-0" element="div" size="small" />}
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow data-state={row.getIsSelected() && 'selected'} key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.maxSize,
                    }}
                    className="truncate"
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            noResultsComponent && (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={columnsLength}>
                  {noResultsComponent || '-'}
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export {
  type ColumnFiltersState,
  type RowSelectionState,
  getPaginationRowModel,
  type PaginationState,
  type TableInterface,
  getFilteredRowModel,
  createColumnHelper,
  type SortingState,
  getSortedRowModel,
  type TableState,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
  type Column,
  DataTable,
  type Row,
};
